import { Info } from '@mui/icons-material';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import DownloadIcon from '@mui/icons-material/Download';
import { Badge, Fade, Popper, Button as ButtonMUI, IconButton } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { IDemandViewModel, IDemandProposalViewModal, ISolicitationViewModel } from 'viewModels';
import { Button } from '..';
import { ReactComponent as IconChecked } from '../../assets/icons/check-message.svg';
import { ReactComponent as IconBell } from '../../assets/icons/IconaweSomeBell.svg';
import { useAuth, useNotification } from '../../context';
import { NotificationController } from '../../controller/NotificationController';
import useClickOutside from '../../hooks/useClickOutside';
import { NotificationType } from '../../types/enums';
import { Container, Content, Notification, NotificationButton } from './styles';
import { ModalDetailsService } from '../Modals/ModalDetailsSolicitation';
import { useSolicitation } from '../../context/solicitation';
import { useAlert } from '../../hooks/useAlert';
import { useConversation } from '../../context/conversation';
import { ModalChat } from '../Modals/ModalChat';
import { SolicitationController } from '../../controller/SolicitationController';
import { downloadReportById } from '../../controller/ReportController';

export function NotificationHeader() {
  const theme = useTheme();
  const { user } = useAuth();
  const alert = useAlert();
  const { notifications, fetchNextPage, hasNextPage, setNotifications, setEnableFetch } =
    useNotification();
  const { solicitations, demandProposals, demands } = useSolicitation();
  const { conversations, refetchConversations } = useConversation();
  const [open, setOpen] = useState(false);
  const [isOpenServiceModal, setOpenServiceModal] = useState(false);
  const [selectedSolicitation, setSelectedSolicitation] = useState<ISolicitationViewModel>();
  const [selectedDemandProposal, setSelectedDemandProposal] = useState<IDemandProposalViewModal>();
  const [selectedDemand, setSelectedDemand] = useState<IDemandViewModel>();
  const [isOpenChatModal, setOpenChatModal] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string>();
  const ref = useRef<HTMLDivElement>(null);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const popperId = open ? 'notification-popper' : undefined;

  useClickOutside(() => setOpen(false), ref);

  const notificationCounter = useMemo(() => {
    if (!Array.isArray(notifications)) return 0;
    return notifications.filter((n) => !n.readed).length;
  }, [notifications]);

  const handleSetSelectedService = (
    type: 'solicitation' | 'demand' | 'demandProposal',
    service: ISolicitationViewModel | IDemandViewModel | IDemandProposalViewModal,
  ) => {
    const setters = {
      solicitation: setSelectedSolicitation,
      demand: setSelectedDemand,
      demandProposal: setSelectedDemandProposal,
    };

    setters[type](service as any);
  };

  const fetchAndSelect = async (
    localList: ISolicitationViewModel[] | IDemandViewModel[] | IDemandProposalViewModal[],
    id: string,
    serviceType: 'solicitation' | 'demand' | 'demandProposal',
  ) => {
    let service = (localList as any[]).find((s) => s.id === id);
    if (!service && serviceType === 'solicitation') {
      service = await SolicitationController.getById(id);
    } else if (!service && serviceType === 'demand') {
      service = await SolicitationController.getDemandById(id);
    } else if (!service && serviceType === 'demandProposal') {
      service = await SolicitationController.getDemandProposalById(id);
    }

    if (service) {
      handleSetSelectedService(serviceType, service);
    }

    return service;
  };

  const handleOpenService = async (type: NotificationType, id: string) => {
    let findService:
      | ISolicitationViewModel
      | IDemandViewModel
      | IDemandProposalViewModal
      | undefined;

    const solicitationTypes = [
      NotificationType.SENDED_NEW_DEMAND,
      NotificationType.SOLICITATION_CONCLUSION,
      NotificationType.NEW_SOLICITATION,
      NotificationType.DEMAND_COUNTER_PROPOSAL,
    ];

    if (solicitationTypes.includes(type)) {
      findService = await fetchAndSelect(solicitations, id, 'solicitation');
    } else if (type === NotificationType.NEW_DEMAND) {
      findService =
        (await fetchAndSelect(demandProposals, id, 'demandProposal')) ||
        (await fetchAndSelect(demands, id, 'demand'));
    }

    setOpen(false);

    if (!findService) {
      alert.show({
        message: `Não foi possível localizar a ${type === NotificationType.NEW_SOLICITATION ? 'solicitação' : 'demanda'
          }`,
        type: 'error',
      });
      return;
    }

    setOpenServiceModal(true);
  };

  const handleRequestClose = () => {
    setOpenServiceModal(false);
    setSelectedSolicitation(undefined);
    setSelectedDemandProposal(undefined);
    setSelectedDemand(undefined);
  };

  const handleOpenChat = (conversationId: string) => {
    let conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) {
      refetchConversations();
      conversation = conversations.find((c) => c.id === conversationId);
      if (!conversation) {
        alert.show({
          message: 'Não foi possível localizar a conversa',
          type: 'error',
        });
        return;
      }
    }
    setSelectedConversationId(conversationId);
    setOpenChatModal(true);
  };

  const handleDownloadReport = async (reportId: string) => {
    try {
      await downloadReportById(reportId);
      alert.show({ message: 'Relatório baixado com sucesso', type: 'success' });
    } catch (error) {
      alert.show({ message: 'Erro ao baixar relatório', type: 'error' });
    }
  };
  const handleRequestCloseChatModal = () => {
    setOpenChatModal(false);
    setSelectedConversationId(undefined);
  };
  const handleLoadMore = async () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const handleClick = () => {
    setOpen(!open);
    setEnableFetch(true);
  };
  const handleMarkAsRead = (id: string) => NotificationController.markAsRead(id);
  const handleMarkAllAsRead = () => {
    NotificationController.markAllAsRead(user.id).then(() => {
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readed: true,
        })),
      );
    });
  };
  return (
    <Container>
      <ButtonMUI ref={anchorEl} aria-describedby={popperId} onClick={handleClick}>
        <Badge
          badgeContent={notificationCounter}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.colors.yellow,
            },
          }}>
          <IconBell className="bellNotification" />
        </Badge>
      </ButtonMUI>

      <Popper
        ref={ref}
        open={open}
        id={popperId}
        anchorEl={anchorEl.current}
        placement="bottom-end"
        transition
        sx={{ zIndex: 1500 }}>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Content>
              <div className="headerNotifications">
                <h1>Notificações</h1>
                <Button
                  text="Marcar todas como lidas"
                  LeftComponent={IconChecked}
                  positionLeftElement={0}
                  styleLeftComponent={{
                    width: '0.8rem',
                    height: '0.9rem',
                    fill: theme.colors.primary,
                  }}
                  font={{ size: 18 }}
                  dimensions={{
                    width: '13rem',
                    height: '2rem',
                  }}
                  backgroundHoverColor="gray_200"
                  textHoverColor="primary"
                  border={{ color: 'transparent', width: 0 }}
                  onClick={handleMarkAllAsRead}
                />
              </div>
              <div className="containerNotification">
                {notifications.length === 0 && (
                  <p className="noNotification">Nenhuma notificação encontrada...</p>
                )}
                {notifications.length > 0 &&
                  notifications.map((notification) => {
                    const isServiceNotification = [
                      NotificationType.NEW_SOLICITATION,
                      NotificationType.NEW_DEMAND,
                      NotificationType.SENDED_NEW_DEMAND,
                      NotificationType.DEMAND_COUNTER_PROPOSAL,
                      NotificationType.SOLICITATION_CONCLUSION,
                    ].includes(notification.type);

                    const isMessageNotification =
                      notification.type === NotificationType.NEW_MESSAGE;

                    const isReportNotification =
                      notification.type === NotificationType.REPORT_READY;

                    const handleClick = () => {
                      if (isServiceNotification && notification.serviceId) {
                        handleOpenService(notification.type, notification.serviceId);
                      } else if (isMessageNotification && notification.conversationId) {
                        handleOpenChat(notification.conversationId);
                      } else if (isReportNotification && notification.reportId) {
                        handleDownloadReport(notification.reportId);
                      }
                    };

                    const renderContent = () => {
                      if (
                        (isServiceNotification && notification.serviceId) ||
                        isMessageNotification ||
                        isReportNotification
                      ) {
                        return (
                          <NotificationButton onClick={handleClick}>
                            <p>{notification.title}</p>
                          </NotificationButton>
                        );
                      }
                      return <p>{notification.title}</p>;
                    };

                    return (
                      <Notification key={notification.id} read={notification.readed}>
                        <p>
                          {notification.type === NotificationType.NEW_SOLICITATION ? (
                            <StickyNote2Icon />
                          ) : notification.type === NotificationType.REPORT_READY ? (
                            <DownloadIcon />
                          ) : (
                            <Info />
                          )}
                        </p>

                        {renderContent()}

                        {!notification.readed && (
                          <IconButton onClick={() => handleMarkAsRead(notification.id)}>
                            <IconChecked />
                          </IconButton>
                        )}
                      </Notification>
                    );
                  })}
                {hasNextPage && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '1rem',
                    }}>
                    <Button
                      text="Carregar mais"
                      dimensions={{
                        width: '8rem',
                        height: '2rem',
                      }}
                      backgroundHoverColor="gray_200"
                      textHoverColor="primary"
                      border={{ color: 'primary', width: 1 }}
                      onClick={handleLoadMore}
                    />
                  </div>
                )}
              </div>
            </Content>
          </Fade>
        )}
      </Popper>
      <ModalDetailsService
        onRequestClose={handleRequestClose}
        solicitation={selectedSolicitation}
        demandProposal={selectedDemandProposal}
        demand={selectedDemand}
        isOpen={isOpenServiceModal}
      />
      <ModalChat
        isOpen={isOpenChatModal}
        onRequestClose={handleRequestCloseChatModal}
        conversationId={selectedConversationId}
      />
    </Container>
  );
}
